import React, { useState, useEffect } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext';

const InstallPrompt = () => {
  const { darkMode } = useContext(ThemeContext);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Detect if app is already installed (standalone mode)
    const standalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(standalone);

    // Listen for beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show iOS install prompt after a delay
    if (iOS && !standalone) {
      setTimeout(() => {
        setShowInstallPrompt(true);
      }, 5000); // Show after 5 seconds on iOS
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Android/Chrome installation
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setShowInstallPrompt(false);
      }
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    localStorage.setItem('installPromptDismissed', Date.now().toString());
  };

  // Don't show if already installed or recently dismissed
  if (isStandalone || !showInstallPrompt) {
    return null;
  }

  const lastDismissed = localStorage.getItem('installPromptDismissed');
  if (lastDismissed && Date.now() - parseInt(lastDismissed) < 7 * 24 * 60 * 60 * 1000) {
    return null; // Don't show for 7 days after dismissal
  }

  return (
    <Alert 
      variant={darkMode ? "dark" : "info"} 
      className={`m-3 border install-prompt d-print-none ${darkMode ? 'dark-mode' : ''}`}
      dismissible 
      onClose={handleDismiss}
      style={{
        backgroundColor: darkMode ? '#2d3748' : '#d1ecf1',
        borderColor: darkMode ? '#4a5568' : '#bee5eb',
        color: darkMode ? '#e2e8f0' : '#0c5460',
        position: 'fixed',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        width: '90%',
        maxWidth: '500px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
      }}
    >
      <Alert.Heading className="d-flex align-items-center">
        <i className="fas fa-mobile-alt me-2"></i>
        Installa Panify Cloud
      </Alert.Heading>
      
      {isIOS ? (
        <div>
          <p className="mb-3">Aggiungi Panify Cloud alla schermata home per un accesso rapido:</p>
          <ol className="mb-3">
            <li className="mb-2">
              Tocca il pulsante <strong>Condividi</strong> <i className="fas fa-share"></i> nella barra di Safari in basso
            </li>
            <li className="mb-2">
              Scorri verso il basso e tocca <strong>"Aggiungi alla schermata Home"</strong> <i className="fas fa-plus-square"></i>
            </li>
            <li className="mb-2">
              Tocca <strong>"Aggiungi"</strong> per confermare
            </li>
          </ol>
          <p className="small text-muted">
            <i className="fas fa-info-circle me-1"></i>
            Una volta installata, l'app apparirà come un'applicazione nativa sulla tua schermata home.
          </p>
        </div>
      ) : (
        <div>
          <p className="mb-3">Installa Panify Cloud sul tuo dispositivo per un accesso rapido e funzionalità offline.</p>
          <div className="d-flex gap-2">
            <Button variant="primary" onClick={handleInstallClick} size="sm">
              <i className="fas fa-download me-2"></i>
              Installa App
            </Button>
            <Button variant="outline-secondary" onClick={handleDismiss} size="sm">
              <i className="fas fa-times me-2"></i>
              Non ora
            </Button>
          </div>
        </div>
      )}
    </Alert>
  );
};

export default InstallPrompt;
